import React, { useState, useEffect, useRef } from 'react';
import MobileVinScanner from '../components/MobileVinScanner';
import ChatGPTPanel from '../components/ChatGPTPanel';

// Add custom CSS animations for VIN validation
const customStyles = `
  @keyframes scale-in {
    0% {
      transform: scale(0) rotate(0deg);
    }
    50% {
      transform: scale(1.2) rotate(180deg);
    }
    100% {
      transform: scale(1) rotate(360deg);
    }
  }
  
  @keyframes shake {
    0%, 20%, 40%, 60%, 80%, 100% {
      transform: translateX(0);
    }
    10%, 30%, 50%, 70%, 90% {
      transform: translateX(-2px);
    }
  }
  
  .animate-scale-in {
    animation: scale-in 0.3s ease-out;
  }
  
  .animate-shake {
    animation: shake 0.5s ease-in-out;
  }
`;

// Inject styles into the document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = customStyles;
  document.head.appendChild(styleSheet);
}

// NHTSA VIN validation with check digit verification
const validateVIN = (vin) => {
  // Remove spaces and convert to uppercase
  const cleanVin = vin.replace(/\s/g, '').toUpperCase();
  
  // Check if VIN is 17 characters
  if (cleanVin.length !== 17) {
    return { valid: false, error: 'VIN must be 17 characters long' };
  }
  
  // Check for invalid characters (I, O, Q are not allowed)
  const invalidChars = /[IOQ]/g;
  if (invalidChars.test(cleanVin)) {
    return { valid: false, error: 'VIN contains invalid characters (I, O, Q not allowed)' };
  }
  
  // Check for valid characters (alphanumeric)
  const validChars = /^[A-HJ-NPR-Z0-9]{17}$/;
  if (!validChars.test(cleanVin)) {
    return { valid: false, error: 'VIN contains invalid characters' };
  }
  
  // VIN check digit validation (9th character)
  const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  const transliteration = {
    'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8,
    'J': 1, 'K': 2, 'L': 3, 'M': 4, 'N': 5, 'P': 7, 'R': 9, 'S': 2,
    'T': 3, 'U': 4, 'V': 5, 'W': 6, 'X': 7, 'Y': 8, 'Z': 9
  };
  
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const char = cleanVin[i];
    let value;
    
    if (char >= '0' && char <= '9') {
      value = parseInt(char);
    } else {
      value = transliteration[char];
      if (value === undefined) {
        return { valid: false, error: 'VIN contains invalid characters' };
      }
    }
    
    sum += value * weights[i];
  }
  
  const checkDigit = sum % 11;
  const expectedCheckDigit = checkDigit === 10 ? 'X' : checkDigit.toString();
  const actualCheckDigit = cleanVin[8];
  
  if (actualCheckDigit !== expectedCheckDigit) {
    return { valid: false, error: 'Invalid VIN - check digit does not match' };
  }
  
  return { valid: true, cleanVin };
};

// ZIP code validation
const validateZipCode = (zipCode) => {
  if (!zipCode) return { valid: false, error: 'ZIP code is required' };
  
  // Remove spaces and hyphens
  const cleanZip = zipCode.replace(/[\s-]/g, '');
  
  // US ZIP code validation: 5 digits or 5+4 format
  const zipPattern = /^\d{5}(\d{4})?$/;
  if (!zipPattern.test(cleanZip)) {
    return { valid: false, error: 'Please enter a valid ZIP code (5 digits or 5+4 format)' };
  }
  
  return { valid: true, cleanZip };
};

// Phone number validation
const validatePhoneNumber = (phone) => {
  if (!phone) return { valid: false, error: 'Phone number is required' };
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Must be 10 digits (US format)
  if (digitsOnly.length !== 10) {
    return { valid: false, error: 'Please enter a 10-digit phone number' };
  }
  
  // First digit cannot be 0 or 1
  if (digitsOnly[0] === '0' || digitsOnly[0] === '1') {
    return { valid: false, error: 'Phone number cannot start with 0 or 1' };
  }
  
  // Area code (first 3 digits) cannot start with 0 or 1
  if (digitsOnly[0] === '0' || digitsOnly[0] === '1') {
    return { valid: false, error: 'Area code cannot start with 0 or 1' };
  }
  
  return { valid: true, cleanPhone: digitsOnly };
};

// Phone number formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedDigits.length === 0) return '';
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

// NHTSA API VIN decoder
const decodeVINWithNHTSA = async (vin) => {
  const validation = validateVIN(vin);
  if (!validation.valid) {
    return validation;
  }
  
  try {
    const response = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${validation.cleanVin}?format=json`);
    const data = await response.json();
    
    if (data.Results && data.Results[0]) {
      const result = data.Results[0];
      
      // Extract key information
      return {
        valid: true,
        vin: validation.cleanVin,
        year: result.ModelYear || 'Unknown',
        make: result.Make || 'Unknown',
        model: result.Model || 'Unknown',
        trim: result.Trim || 'Unknown',
        bodyClass: result.BodyClass || 'Unknown',
        vehicleType: result.VehicleType || 'Unknown',
        engineCylinders: result.EngineCylinders || 'Unknown',
        engineHP: result.EngineHP || 'Unknown',
        fuelType: result.FuelTypePrimary || 'Unknown',
        manufacturer: result.Manufacturer || 'Unknown',
        plantCountry: result.PlantCountry || 'Unknown',
        driveType: result.DriveType || 'Unknown',
        doors: result.Doors || 'Unknown',
        transmission: result.TransmissionStyle || 'Unknown',
        series: result.Series || 'Unknown',
        errorCode: result.ErrorCode,
        rawData: result
      };
    } else {
      return { valid: false, error: 'Could not decode VIN with NHTSA database' };
    }
  } catch (error) {
    console.error('NHTSA API Error:', error);
    return { valid: false, error: 'Failed to connect to NHTSA database' };
  }
};

export default function TradeInEvaluation() {
  const [vin, setVin] = useState('');
  const [vinResult, setVinResult] = useState(null);
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [decodingVin, setDecodingVin] = useState(false);
  const [vinValidation, setVinValidation] = useState(null);
  const [showVinScanner, setShowVinScanner] = useState(false);

  // Platform detection - disable QR scanning for Windows
  const isWindows = navigator.platform.toLowerCase().includes('win');

  // Contact + Vehicle Status Form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    currentMiles: '',
    vehicleColor: '',
    sellingOption: '',
    financingStatus: '',
    payoffAmount: '',
    loanCompany: ''
  });

  // Features & Options Form
  const [featuresData, setFeaturesData] = useState({
    // Comfort & Convenience
    keylessEntry: false,
    powerMirrors: false,
    cruiseControl: false,
    remoteStart: false,
    // Entertainment
    androidAppleCarplay: false,
    premiumSoundSystem: false,
    // Exterior
    premiumWheels: false,
    aftermarketWheels: false,
    tintedWindows: false
  });

  // Condition & History Form
  const [conditionData, setConditionData] = useState({
    exteriorDamage: '',
    interiorDamage: '',
    windshieldCondition: '',
    moonroofOperation: '',
    smokedInOdors: '',
    vehicleModifications: '',
    accidentHistory: '',
    vehicleDrivability: '',
    mechanicalElectricalIssues: '',
    engineIssues: '',
    serviceHistory: '',
    otherServiceIssues: '',
    keysAvailable: '',
    // Tire replacement
    tiresReplaced: false,
    originalTires: true,
    replacementMiles: '',
    replacementCount: ''
  });

  // Photo upload
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form validation state
  const [missingFields, setMissingFields] = useState([]);

  // Animation refs for space background
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const starsRef = useRef([]);

  useEffect(() => {
    // Get dealership identifier from URL (could be name or ID)
    const path = window.location.pathname;
    const dealershipIdentifier = path.split('/')[2];
    
    if (dealershipIdentifier) {
      fetchDealership(dealershipIdentifier);
    } else {
      setError('Invalid dealership identifier');
      setLoading(false);
    }
  }, []);

  // Initialize stars
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Create stars
    const stars = [];
    for (let i = 0; i < 150; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.3 + 0.05,
        opacity: Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * 0.02 + 0.005
      });
    }
    starsRef.current = stars;
    
    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const stars = starsRef.current;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw moving stars
      stars.forEach(star => {
        // Move stars
        star.y += star.speed;
        if (star.y > canvas.height) {
          star.y = -5;
          star.x = Math.random() * canvas.width;
        }
        
        // Twinkle effect
        star.opacity += star.twinkle;
        if (star.opacity > 1 || star.opacity < 0.1) {
          star.twinkle = -star.twinkle;
        }
        
        // Mouse interaction - subtle attraction to mouse
        const dx = mouseRef.current.x - star.x;
        const dy = mouseRef.current.y - star.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = (100 - distance) / 100;
          star.x += dx * force * 0.005;
          star.y += dy * force * 0.005;
        }
        
        // Draw star
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
        
        // Add subtle glow effect
        if (star.size > 1) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 1.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(135, 206, 250, ${star.opacity * 0.2})`;
          ctx.fill();
        }
      });
      
      // Draw subtle nebula effect around mouse
      const gradient = ctx.createRadialGradient(
        mouseRef.current.x, mouseRef.current.y, 0,
        mouseRef.current.x, mouseRef.current.y, 150
      );
      gradient.addColorStop(0, 'rgba(138, 43, 226, 0.03)');
      gradient.addColorStop(0.5, 'rgba(75, 0, 130, 0.02)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const fetchDealership = async (dealershipIdentifier) => {
    try {
      // First try to get dealership by name (for URLs like /trade-in/wiers)
      let response = await fetch(`/api/dealerships/by-name/${dealershipIdentifier}`);
      
      // If not found by name, try by ID (fallback for existing URLs)
      if (!response.ok) {
        response = await fetch(`/api/dealerships/${dealershipIdentifier}`);
      }
      
      if (response.ok) {
        const dealershipData = await response.json();
        setDealership(dealershipData);
      } else {
        setError('Dealership not found');
      }
    } catch (err) {
      setError('Error loading dealership information');
    } finally {
      setLoading(false);
    }
  };

  const checkVinExists = async (vin, dealershipId) => {
    try {
      const response = await fetch(`/api/trade-in-evaluations/check-vin?vin=${vin}&dealershipId=${dealershipId}`);
      const result = await response.json();
      return result.exists;
    } catch (error) {
      console.error('Error checking VIN:', error);
      return false; // If there's an error, allow the user to proceed
    }
  };

  const handleVinSubmit = async (e) => {
    e.preventDefault();
    if (!vin.trim()) {
      setError('Please enter a VIN');
      return;
    }
    
    setDecodingVin(true);
    setError(null);
    
    const result = await decodeVINWithNHTSA(vin);
    setVinResult(result);
    
    if (result.valid) {
      // Check if VIN already exists for this dealership
      const path = window.location.pathname;
      const dealershipIdentifier = path.split('/')[2];
      
      // Use the dealership ID from the fetched dealership object
      const dealershipId = dealership?._id || dealershipIdentifier;
      const vinExists = await checkVinExists(vin, dealershipId);
      
      if (vinExists) {
        setError('This VIN has already been submitted for evaluation. Please contact the dealership if you need to update your submission.');
        setDecodingVin(false);
        return;
      }
      
      setDecodingVin(false);
      setCurrentStep(2);
    } else {
      setDecodingVin(false);
      setError(result.error);
    }
  };

  const handleVinChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
    if (value.length <= 17) {
      setVin(value);
      setError(null);
      
      // Real-time VIN validation
      if (value.length === 0) {
        setVinValidation(null);
      } else if (value.length < 17) {
        setVinValidation({ valid: false, error: 'VIN must be 17 characters', partial: true });
      } else if (value.length === 17) {
        const validation = validateVIN(value);
        setVinValidation(validation);
      }
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // If financing status changes, handle payoff amount and loan company accordingly
      if (field === 'financingStatus') {
        if (value === 'paid') {
          // Clear payoff amount and loan company when vehicle is paid off
          newData.payoffAmount = '';
          newData.loanCompany = '';
        }
      }
      
      // Apply phone number formatting
      if (field === 'phone') {
        newData.phone = formatPhoneNumber(value);
      }
      
      return newData;
    });
  };

  const handleFeatureChange = (feature, value) => {
    setFeaturesData(prev => ({
      ...prev,
      [feature]: value
    }));
  };

  const handleConditionChange = (field, value) => {
    setConditionData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Remove field from missing fields list if user provides a value
    if (value && missingFields.includes(field)) {
      setMissingFields(prev => prev.filter(f => f !== field));
      // Clear error if this was the last missing field
      if (missingFields.length === 1) {
        setError(null);
      }
    }
  };

  const handlePhotoUpload = (files) => {
    const newPhotos = Array.from(files).map(file => ({
      file,
      url: URL.createObjectURL(file),
      id: Date.now() + Math.random()
    }));
    setUploadedPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (photoId) => {
    setUploadedPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };

  const handleContactFormSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    
        if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone number
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.valid) {
      setError(phoneValidation.error);
      return;
    }
    
    // Validate zip code
    const zipValidation = validateZipCode(formData.zipCode);
    if (!zipValidation.valid) {
      setError(zipValidation.error);
      return;
    }
    
    if (!formData.currentMiles.trim()) {
      setError('Current miles is required');
      return;
    }

    if (!formData.vehicleColor.trim()) {
      setError('Vehicle color is required');
      return;
    }

    if (!formData.sellingOption) {
      setError('Please select a selling/trading option');
      return;
    }

    if (!formData.financingStatus) {
      setError('Please select your financing status');
      return;
    }

    if (formData.financingStatus === 'financed' && !formData.payoffAmount.trim()) {
      setError('Payoff amount is required for financed/leased vehicles');
      return;
    }

    if (formData.financingStatus === 'financed' && !formData.loanCompany.trim()) {
      setError('Loan company is required for financed/leased vehicles');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Move to next step
    setCurrentStep(3);
  };

  const handleFeaturesFormSubmit = (e) => {
    e.preventDefault();
    setCurrentStep(4);
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required condition fields
    const requiredFields = [];
    
    if (!conditionData.exteriorDamage) {
      requiredFields.push('exteriorDamage');
    }

    if (!conditionData.interiorDamage) {
      requiredFields.push('interiorDamage');
    }

    if (!conditionData.windshieldCondition) {
      requiredFields.push('windshieldCondition');
    }

    if (!conditionData.moonroofOperation) {
      requiredFields.push('moonroofOperation');
    }

    if (!conditionData.smokedInOdors) {
      requiredFields.push('smokedInOdors');
    }

    if (!conditionData.accidentHistory) {
      requiredFields.push('accidentHistory');
    }

    if (!conditionData.vehicleDrivability) {
      requiredFields.push('vehicleDrivability');
    }

    if (!conditionData.keysAvailable) {
      requiredFields.push('keysAvailable');
    }

    if (!conditionData.mechanicalElectricalStatus) {
      requiredFields.push('mechanicalElectricalStatus');
    }

    if (!conditionData.serviceHistoryStatus) {
      requiredFields.push('serviceHistoryStatus');
    }

    if (!conditionData.engineIssuesStatus) {
      requiredFields.push('engineIssuesStatus');
    }

    if (!conditionData.otherServiceIssuesStatus) {
      requiredFields.push('otherServiceIssuesStatus');
    }
    
    // If there are missing fields, show them and don't submit
    if (requiredFields.length > 0) {
      setMissingFields(requiredFields);
      setError(`Please complete the following required fields: ${requiredFields.map(field => {
        switch(field) {
          case 'exteriorDamage': return 'Exterior Damage';
          case 'interiorDamage': return 'Interior Damage';
          case 'windshieldCondition': return 'Windshield Condition';
          case 'moonroofOperation': return 'Moonroof Operation';
          case 'smokedInOdors': return 'Smoked In Or Odors';
          case 'accidentHistory': return 'Accident History';
          case 'vehicleDrivability': return 'Vehicle Drivability';
          case 'keysAvailable': return 'Keys Available';
          case 'mechanicalElectricalStatus': return 'Mechanical & Electrical';
          case 'serviceHistoryStatus': return 'Service History';
          case 'engineIssuesStatus': return 'Engine Issues';
          case 'otherServiceIssuesStatus': return 'Other Service Issues';
          default: return field;
        }
      }).join(', ')}`);
      
      // Scroll to the first missing field
      const firstMissingField = document.querySelector(`[data-field="${requiredFields[0]}"]`);
      if (firstMissingField) {
        firstMissingField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      setSubmitting(false);
      return;
    }
    
    // Clear any previous missing fields if validation passes
    setMissingFields([]);
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Get dealership ID from URL or use the fetched dealership object
      const path = window.location.pathname;
      const dealershipIdentifier = path.split('/')[2];
      const dealershipId = dealership?._id || dealershipIdentifier;
      
      // Create FormData for file uploads
      const submitData = new FormData();
      
      // Add basic data
      submitData.append('dealershipId', dealershipId);
      submitData.append('vehicleInfo', JSON.stringify({
        vin: vinResult.vin,
        year: vinResult.year,
        make: vinResult.make,
        model: vinResult.model,
        trim: vinResult.trim,
        bodyClass: vinResult.bodyClass,
        vehicleType: vinResult.vehicleType,
        engineCylinders: vinResult.engineCylinders,
        engineHP: vinResult.engineHP,
        fuelType: vinResult.fuelType,
        manufacturer: vinResult.manufacturer,
        plantCountry: vinResult.plantCountry,
        driveType: vinResult.driveType,
        doors: vinResult.doors,
        transmission: vinResult.transmission,
        series: vinResult.series,
        currentMiles: parseInt(formData.currentMiles),
        vehicleColor: formData.vehicleColor
      }));
      
      submitData.append('customerInfo', JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone.replace(/\D/g, ''), // Store clean digits only
        zipCode: formData.zipCode,
        sellingOption: formData.sellingOption,
        financingStatus: formData.financingStatus,
        hasPayoff: formData.financingStatus === 'financed' ? 'yes' : 'no',
        payoffAmount: formData.financingStatus === 'financed' && formData.payoffAmount ? parseFloat(formData.payoffAmount) : 0,
        loanCompany: formData.financingStatus === 'financed' && formData.loanCompany ? formData.loanCompany.trim() : ''
      }));
      
      submitData.append('features', JSON.stringify(featuresData));
      submitData.append('condition', JSON.stringify(conditionData));
      
      // Add photos
      uploadedPhotos.forEach(photo => {
        submitData.append('photos', photo.file);
      });
      
      // Submit to API
      console.log('Submitting to API...');
      const response = await fetch('/api/trade-in-evaluations', {
        method: 'POST',
        body: submitData
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers.get('content-type'));
      
      if (!response.ok) {
        // Handle non-200 responses
        const text = await response.text();
        console.error('Server error response:', text);
        throw new Error(`Server error: ${response.status} - ${response.statusText}`);
      }
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned an invalid response format');
      }
      
      const result = await response.json();
      console.log('Parsed response:', result);
      
      if (result.success) {
        setSubmitting(false);
        setSubmitted(true);
        
        // After 3 seconds, redirect to trade-in evaluation homepage
        setTimeout(() => {
          // Use dealership name instead of ID, take only first word if there are spaces
          const dealershipName = dealership?.name ? dealership.name.split(' ')[0].toLowerCase() : dealershipIdentifier;
          window.location.href = `/trade-in/${dealershipName}`;
        }, 3000);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
      
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      setSubmitting(false);
      setError('Failed to submit evaluation. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Animated Space Background */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a3e 25%, #26314e 50%, #1f4470 75%, #634493 100%)'
          }}
        />
        
        <div className="relative z-10 min-h-screen flex items-center justify-center text-white">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg">Loading dealership information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !dealership) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Animated Space Background */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full"
          style={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a3e 25%, #26314e 50%, #1f4470 75%, #634493 100%)'
          }}
        />
        
        <div className="relative z-10 min-h-screen flex items-center justify-center text-white">
          <div className="text-center">
            <div className="text-red-400 text-xl mb-4">Error</div>
            <p className="text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Space Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a3e 25%, #26314e 50%, #1f4470 75%, #634493 100%)'
        }}
      />
      
      {/* Floating particles overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-purple-400 rounded-full animate-ping"></div>
        <div className="absolute top-3/4 right-1/4 w-0.5 h-0.5 bg-blue-400 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/6 w-1 h-1 bg-cyan-400 rounded-full animate-bounce"></div>
        <div className="absolute top-1/6 right-1/3 w-0.5 h-0.5 bg-purple-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-1/4 left-1/3 w-1 h-1 bg-indigo-400 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen text-white p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-4 drop-shadow-lg">
              Vehicle Trade-In Evaluation
            </h1>
          </div>

          {/* Progress Steps */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${currentStep >= 1 ? 'text-blue-400' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= 1 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-300'
                }`}>
                  1
                </div>
                <span className="ml-3 font-medium">VIN Lookup</span>
              </div>
              <div className={`flex-1 h-1 mx-4 ${currentStep >= 2 ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
              <div className={`flex items-center ${currentStep >= 2 ? 'text-blue-400' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= 2 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-300'
                }`}>
                  2
                </div>
                <span className="ml-3 font-medium">Vehicle Details</span>
              </div>
              <div className={`flex-1 h-1 mx-4 ${currentStep >= 3 ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
              <div className={`flex items-center ${currentStep >= 3 ? 'text-blue-400' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= 3 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-300'
                }`}>
                  3
                </div>
                <span className="ml-3 font-medium">Features & Options</span>
              </div>
              <div className={`flex-1 h-1 mx-4 ${currentStep >= 4 ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
              <div className={`flex items-center ${currentStep >= 4 ? 'text-blue-400' : 'text-gray-500'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  currentStep >= 4 ? 'bg-blue-500 text-white' : 'bg-gray-600 text-gray-300'
                }`}>
                  4
                </div>
                <span className="ml-3 font-medium">Condition & History</span>
              </div>
            </div>
          </div>

          {/* Step 1: VIN Decoder */}
          {currentStep === 1 && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-8 shadow-2xl">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
                  Enter Your Vehicle's VIN
                </h2>
                
                <div className="mb-6">
                  <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-blue-400 mr-3 mt-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-blue-200">
                          <strong>What is a VIN?</strong> A Vehicle Identification Number (VIN) is a unique 17-character code that identifies your vehicle. 
                          You can find it on your dashboard (visible through the windshield), driver's side door jamb, or vehicle registration.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleVinSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="vin" className="block text-sm font-medium text-gray-300 mb-3">
                      Vehicle Identification Number (VIN)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="vin"
                        value={vin}
                        onChange={handleVinChange}
                        placeholder="Enter 17-character VIN (e.g., 1HGBH41JXMN109186)"
                        className={`w-full px-4 py-4 pr-12 bg-white/15 border rounded-lg focus:ring-2 focus:border-transparent text-center text-lg font-mono tracking-wider text-white placeholder-gray-400 backdrop-blur-sm transition-all duration-200 ${
                          vinValidation === null 
                            ? 'border-white/40 focus:ring-blue-500' 
                            : vinValidation.valid 
                            ? 'border-green-500 focus:ring-green-500' 
                            : 'border-red-500 focus:ring-red-500'
                        }`}
                        maxLength="17"
                        disabled={decodingVin}
                      />
                      {vinValidation !== null && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          {vinValidation.valid ? (
                            <svg className="w-6 h-6 text-green-400 animate-scale-in" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-red-400 animate-shake" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-center text-sm mt-2 flex items-center justify-center">
                      <span className="text-gray-400">
                        {vin.length}/17 characters
                      </span>
                      {vinValidation !== null && !vinValidation.partial && (
                        <span className={`ml-3 text-sm ${vinValidation.valid ? 'text-green-400' : 'text-red-400'}`}>
                          {vinValidation.valid ? '✓ Valid VIN' : '✗ Invalid VIN'}
                        </span>
                      )}
                    </div>
                    
                    {/* Validation message */}
                    {vinValidation !== null && !vinValidation.valid && !vinValidation.partial && (
                      <div className="mt-2 text-center">
                        <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                          {vinValidation.error}
                        </p>
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
                      <div className="flex">
                        <div className="text-red-400">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-200">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={decodingVin || !vinValidation?.valid}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl"
                  >
                    {decodingVin ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Decoding VIN...
                      </div>
                    ) : (
                      'Decode VIN & Continue'
                    )}
                  </button>
                </form>

                {/* Mobile VIN Scanner Button - Hidden on Windows */}
                {!isWindows && (
                <div className="mt-6">
                  <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/30 p-4">
                    <h3 className="text-lg font-semibold text-white mb-3 text-center">
                      Or Scan Your VIN
                    </h3>
                      <button
                        type="button"
                        onClick={() => setShowVinScanner(true)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-lg shadow-lg"
                        disabled={decodingVin}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        <span>📱 Scan QR Code</span>
                      </button>
                      <p className="text-sm text-gray-300 mt-2 text-center">
                        Point your camera at a QR code to automatically fill in your VIN
                      </p>
                  </div>
                </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Information & Contact Form */}
          {currentStep === 2 && vinResult && vinResult.valid && (
            <div className="max-w-5xl mx-auto space-y-8">
              {/* Vehicle Information Box */}
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-6 shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center">
                  <svg className="w-8 h-8 mr-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Vehicle Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">VIN</div>
                    <div className="text-white font-mono text-sm">{vinResult.vin}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Year</div>
                    <div className="text-white font-semibold">{vinResult.year}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Make</div>
                    <div className="text-white font-semibold">{vinResult.make}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Model</div>
                    <div className="text-white font-semibold">{vinResult.model}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Trim</div>
                    <div className="text-white font-semibold">{vinResult.trim}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Body Class</div>
                    <div className="text-white font-semibold">{vinResult.bodyClass}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Fuel Type</div>
                    <div className="text-white font-semibold">{vinResult.fuelType}</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                    <div className="text-gray-400 text-sm">Drive Type</div>
                    <div className="text-white font-semibold">{vinResult.driveType}</div>
                  </div>
                </div>
              </div>

              {/* Contact + Vehicle Status Form */}
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-8 shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Contact & Vehicle Information
                </h2>
                
                {/* Error Display */}
                {error && (
                  <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4 mb-6">
                    <div className="flex">
                      <div className="text-red-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-200">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleContactFormSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleFormChange('firstName', e.target.value)}
                        className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleFormChange('lastName', e.target.value)}
                        className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                      placeholder="Enter your email address"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                        className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                        placeholder="(555) 123-4567"
                        inputMode="numeric"
                        pattern="^\(\d{3}\) \d{3}-\d{4}$"
                        maxLength="14"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => handleFormChange('zipCode', e.target.value)}
                        className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                        placeholder="12345 or 12345-6789"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Current Miles
                      </label>
                      <input
                        type="number"
                        value={formData.currentMiles}
                        onChange={(e) => handleFormChange('currentMiles', e.target.value)}
                        className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                        placeholder="Current odometer reading"
                        required
                      />
                    </div>
                  </div>    

                  {/* Vehicle Color Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Vehicle Color
                    </label>
                    <select
                      value={formData.vehicleColor}
                      onChange={(e) => handleFormChange('vehicleColor', e.target.value)}
                      className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white backdrop-blur-sm"
                      style={{ colorScheme: 'dark' }}
                      required
                    >
                      <option value="" style={{ color: '#000' }}>Select vehicle color</option>
                      <option value="White" style={{ color: '#000' }}>White</option>
                      <option value="Black" style={{ color: '#000' }}>Black</option>
                      <option value="Silver" style={{ color: '#000' }}>Silver</option>
                      <option value="Gray" style={{ color: '#000' }}>Gray</option>
                      <option value="Red" style={{ color: '#000' }}>Red</option>
                      <option value="Blue" style={{ color: '#000' }}>Blue</option>
                      <option value="Dark Blue" style={{ color: '#000' }}>Dark Blue</option>
                      <option value="Navy Blue" style={{ color: '#000' }}>Navy Blue</option>
                      <option value="Green" style={{ color: '#000' }}>Green</option>
                      <option value="Dark Green" style={{ color: '#000' }}>Dark Green</option>
                      <option value="Brown" style={{ color: '#000' }}>Brown</option>
                      <option value="Tan" style={{ color: '#000' }}>Tan</option>
                      <option value="Beige" style={{ color: '#000' }}>Beige</option>
                      <option value="Gold" style={{ color: '#000' }}>Gold</option>
                      <option value="Yellow" style={{ color: '#000' }}>Yellow</option>
                      <option value="Orange" style={{ color: '#000' }}>Orange</option>
                      <option value="Purple" style={{ color: '#000' }}>Purple</option>
                      <option value="Maroon" style={{ color: '#000' }}>Maroon</option>
                      <option value="Burgundy" style={{ color: '#000' }}>Burgundy</option>
                      <option value="Charcoal" style={{ color: '#000' }}>Charcoal</option>
                      <option value="Pearl White" style={{ color: '#000' }}>Pearl White</option>
                      <option value="Metallic Silver" style={{ color: '#000' }}>Metallic Silver</option>
                      <option value="Metallic Gray" style={{ color: '#000' }}>Metallic Gray</option>
                      <option value="Midnight Blue" style={{ color: '#000' }}>Midnight Blue</option>
                      <option value="Other" style={{ color: '#000' }}>Other</option>
                    </select>
                  </div>

                  {/* Selling/Trading Options */}
                  <div>
                    <label className="block text-lg font-bold text-white mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                      Selling / Trading Options
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: 'sell', label: 'I want to sell' },
                        { value: 'trade', label: 'I want to trade in' }
                      ].map((option) => (
                        <label key={option.value} className="group cursor-pointer">
                        <input
                          type="radio"
                          name="sellingOption"
                            value={option.value}
                            checked={formData.sellingOption === option.value}
                          onChange={(e) => handleFormChange('sellingOption', e.target.value)}
                            className="sr-only"
                          />
                          <div className={`relative flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.sellingOption === option.value
                              ? 'border-blue-400 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                              : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                          }`}>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-4 transition-all duration-200 ${
                              formData.sellingOption === option.value
                                ? 'bg-blue-500 text-white shadow-lg'
                                : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                            }`}>
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className={`text-lg font-medium transition-colors duration-200 ${
                              formData.sellingOption === option.value ? 'text-white' : 'text-gray-300'
                            }`}>
                              {option.label}
                            </span>
                            {formData.sellingOption === option.value && (
                              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 pointer-events-none" />
                            )}
                          </div>
                      </label>
                      ))}
                    </div>
                  </div>

                  {/* Financing Status */}
                  <div>
                    <label className="block text-lg font-bold text-white mb-4 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                      Financing Status
                    </label>
                    <div className="space-y-3">
                      {[
                        { value: 'financed', label: 'Financed / Leased' },
                        { value: 'paid', label: 'Vehicle is Paid Off' }
                      ].map((option) => (
                        <label key={option.value} className="group cursor-pointer">
                        <input
                          type="radio"
                          name="financingStatus"
                            value={option.value}
                            checked={formData.financingStatus === option.value}
                          onChange={(e) => handleFormChange('financingStatus', e.target.value)}
                            className="sr-only"
                          />
                          <div className={`relative flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.financingStatus === option.value
                              ? 'border-green-400 bg-green-500/20 shadow-lg shadow-green-500/25'
                              : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                          }`}>
                            <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-4 transition-all duration-200 ${
                              formData.financingStatus === option.value
                                ? 'bg-green-500 text-white shadow-lg'
                                : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                            }`}>
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                    </div>
                            <span className={`text-lg font-medium transition-colors duration-200 ${
                              formData.financingStatus === option.value ? 'text-white' : 'text-gray-300'
                            }`}>
                              {option.label}
                            </span>
                            {formData.financingStatus === option.value && (
                              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-500/10 to-blue-500/10 pointer-events-none" />
                            )}
                  </div>
                      </label>
                      ))}
                      </div>
                    </div>

                  {/* Payoff Amount and Loan Company (conditional) */}
                  {formData.financingStatus === 'financed' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Payoff Amount
                        </label>
                        <input
                          type="number"
                          value={formData.payoffAmount}
                          onChange={(e) => handleFormChange('payoffAmount', e.target.value)}
                          className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                          placeholder="Remaining loan/lease balance"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Loan Company
                        </label>
                        <input
                          type="text"
                          value={formData.loanCompany}
                          onChange={(e) => handleFormChange('loanCompany', e.target.value)}
                          className="w-full px-4 py-3 bg-white/15 border border-white/40 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 backdrop-blur-sm"
                          placeholder="e.g., Chase Auto Finance, Capital One, etc."
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 px-6 py-3 border border-white/40 text-white rounded-lg hover:bg-white/15 transition-colors backdrop-blur-sm"
                    >
                      Back to VIN Entry
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-xl"
                    >
                      Continue to Features & Options
                    </button>
                  </div>
                </form>
              </div>
              

            </div>
          )}

          {/* Step 3: Features & Options */}
          {currentStep === 3 && (
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Left Column: Features Form */}
                <div className="xl:col-span-2">
                  <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-8 shadow-2xl">
                    <h2 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center">
                      <svg className="w-8 h-8 mr-3 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Features & Options
                    </h2>
                
                <p className="text-center text-gray-300 mb-8">
                  Select all features and options that your vehicle currently has to get a more accurate evaluation.
                </p>

                <form onSubmit={handleFeaturesFormSubmit} className="space-y-8">
                  {/* Comfort & Convenience */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Comfort & Convenience</h3>
                    </div>
                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.keylessEntry}
                          onChange={(e) => handleFeatureChange('keylessEntry', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.keylessEntry
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.keylessEntry
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.keylessEntry ? 'text-white' : 'text-gray-300'
                          }`}>
                            Keyless Entry
                          </span>
                          {featuresData.keylessEntry && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.powerMirrors}
                          onChange={(e) => handleFeatureChange('powerMirrors', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.powerMirrors
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.powerMirrors
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.powerMirrors ? 'text-white' : 'text-gray-300'
                          }`}>
                            Power Mirrors
                          </span>
                          {featuresData.powerMirrors && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.cruiseControl}
                          onChange={(e) => handleFeatureChange('cruiseControl', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.cruiseControl
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.cruiseControl
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.cruiseControl ? 'text-white' : 'text-gray-300'
                          }`}>
                            Cruise Control
                          </span>
                          {featuresData.cruiseControl && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.remoteStart}
                          onChange={(e) => handleFeatureChange('remoteStart', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.remoteStart
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.remoteStart
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.remoteStart ? 'text-white' : 'text-gray-300'
                          }`}>
                            Remote Start
                          </span>
                          {featuresData.remoteStart && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Entertainment */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Entertainment</h3>
                    </div>
                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.androidAppleCarplay}
                          onChange={(e) => handleFeatureChange('androidAppleCarplay', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.androidAppleCarplay
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.androidAppleCarplay
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.androidAppleCarplay ? 'text-white' : 'text-gray-300'
                          }`}>
                            Android / Apple Auto CarPlay
                          </span>
                          {featuresData.androidAppleCarplay && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.premiumSoundSystem}
                          onChange={(e) => handleFeatureChange('premiumSoundSystem', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.premiumSoundSystem
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.premiumSoundSystem
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.premiumSoundSystem ? 'text-white' : 'text-gray-300'
                          }`}>
                            Premium Sound System
                          </span>
                          {featuresData.premiumSoundSystem && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Exterior */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Exterior</h3>
                    </div>
                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.premiumWheels}
                          onChange={(e) => handleFeatureChange('premiumWheels', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.premiumWheels
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.premiumWheels
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.premiumWheels ? 'text-white' : 'text-gray-300'
                          }`}>
                            Premium Wheels
                          </span>
                          {featuresData.premiumWheels && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.aftermarketWheels}
                          onChange={(e) => handleFeatureChange('aftermarketWheels', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.aftermarketWheels
                            ? 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.aftermarketWheels
                              ? 'bg-red-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.aftermarketWheels ? 'text-white' : 'text-gray-300'
                          }`}>
                            Aftermarket Wheels
                          </span>
                          {featuresData.aftermarketWheels && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-red-400/10 to-orange-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                      <label className="group cursor-pointer">
                        <input
                          type="checkbox"
                          checked={featuresData.tintedWindows}
                          onChange={(e) => handleFeatureChange('tintedWindows', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                          featuresData.tintedWindows
                            ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                            : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                        }`}>
                          <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                            featuresData.tintedWindows
                              ? 'bg-green-400 text-white shadow-lg'
                              : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                          }`}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className={`font-medium transition-colors duration-200 ${
                            featuresData.tintedWindows ? 'text-white' : 'text-gray-300'
                          }`}>
                            Tinted Windows
                          </span>
                          {featuresData.tintedWindows && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-400/10 to-emerald-400/10 pointer-events-none" />
                          )}
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors backdrop-blur-sm"
                    >
                      Back to Vehicle Details
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-xl"
                    >
                      Continue to Condition & History
                    </button>
                  </div>
                </form>
                  </div>
                </div>
                
                {/* Right Column: ChatGPT Panel - Hidden on Mobile */}
                <div className="hidden lg:block xl:col-span-1">
                  <div className="xl:sticky xl:top-8 xl:h-fit">
                    <ChatGPTPanel 
                      vehicleData={{
                        year: vinResult.year,
                        make: vinResult.make,
                        model: vinResult.model,
                        trim: vinResult.trim,
                        bodyClass: vinResult.bodyClass,
                        currentMiles: formData.currentMiles,
                        driveType: vinResult.driveType,
                        vehicleColor: formData.vehicleColor
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Condition & History */}
          {currentStep === 4 && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white/15 backdrop-blur-lg rounded-2xl border border-white/30 p-8 shadow-2xl">
                <h2 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center">
                  <svg className="w-8 h-8 mr-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Condition & History
                </h2>
                
                <p className="text-center text-gray-300 mb-8">
                  Help us accurately assess your vehicle by providing details about its condition and history.
                </p>

                <form onSubmit={handleFinalSubmit} className="space-y-6">
                  {/* Damage & Condition Section */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold">Damage & Condition</h3>
                    </div>
                    <div className="p-4 pt-0 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div data-field="exteriorDamage" className={`${missingFields.includes('exteriorDamage') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('exteriorDamage') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent`}>
                              EXTERIOR DAMAGE {missingFields.includes('exteriorDamage') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: 'none', label: 'No damage' },
                                { value: 'minor', label: 'Minor scratches/dings' },
                                { value: 'major-scratches', label: 'Major Scratches' },
                                { value: 'moderate', label: 'Moderate damage' },
                                { value: 'major', label: 'Major damage' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="exteriorDamage"
                                    value={option.value}
                                    checked={conditionData.exteriorDamage === option.value}
                              onChange={(e) => handleConditionChange('exteriorDamage', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                    conditionData.exteriorDamage === option.value
                                      ? option.value === 'none' 
                                        ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                        : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                      conditionData.exteriorDamage === option.value
                                        ? option.value === 'none'
                                          ? 'bg-green-400 text-white shadow-lg'
                                          : 'bg-red-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                          </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.exteriorDamage === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.exteriorDamage === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.value === 'none' 
                                          ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                          : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div data-field="interiorDamage" className={`${missingFields.includes('interiorDamage') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('interiorDamage') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent`}>
                              INTERIOR DAMAGE {missingFields.includes('interiorDamage') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: 'no', label: 'No' },
                                { value: 'yes', label: 'Yes' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="interiorDamage"
                                    value={option.value}
                                    checked={conditionData.interiorDamage === option.value}
                              onChange={(e) => handleConditionChange('interiorDamage', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                    conditionData.interiorDamage === option.value
                                      ? option.value === 'no' 
                                        ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                        : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                      conditionData.interiorDamage === option.value
                                        ? option.value === 'no'
                                          ? 'bg-green-400 text-white shadow-lg'
                                          : 'bg-red-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                          </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.interiorDamage === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.interiorDamage === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.value === 'no' 
                                          ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                          : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div data-field="windshieldCondition" className={`${missingFields.includes('windshieldCondition') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('windshieldCondition') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent`}>
                              WINDSHIELD CONDITION {missingFields.includes('windshieldCondition') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: 'perfect', label: 'Perfect', condition: 'good' },
                                { value: 'chips', label: 'Minor chips', condition: 'bad' },
                                { value: 'cracks', label: 'Cracks', condition: 'bad' },
                                { value: 'needs-replacement', label: 'Needs replacement', condition: 'bad' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="windshieldCondition"
                                    value={option.value}
                                    checked={conditionData.windshieldCondition === option.value}
                              onChange={(e) => handleConditionChange('windshieldCondition', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                    conditionData.windshieldCondition === option.value
                                      ? option.condition === 'good' 
                                        ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                        : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                      conditionData.windshieldCondition === option.value
                                        ? option.condition === 'good'
                                          ? 'bg-green-400 text-white shadow-lg'
                                          : 'bg-red-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                          </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.windshieldCondition === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.windshieldCondition === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.condition === 'good' 
                                          ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                          : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div data-field="moonroofOperation" className={`${missingFields.includes('moonroofOperation') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('moonroofOperation') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent`}>
                              MOONROOF OPERATION {missingFields.includes('moonroofOperation') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: 'no-moonroof', label: 'No moonroof' },
                                { value: 'works-perfectly', label: 'Works perfectly' },
                                { value: 'not-working', label: 'Not working' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="moonroofOperation"
                                    value={option.value}
                                    checked={conditionData.moonroofOperation === option.value}
                              onChange={(e) => handleConditionChange('moonroofOperation', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                    conditionData.moonroofOperation === option.value
                                      ? option.value === 'not-working' 
                                        ? 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                        : 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                      conditionData.moonroofOperation === option.value
                                        ? option.value === 'not-working'
                                          ? 'bg-red-400 text-white shadow-lg'
                                          : 'bg-green-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.moonroofOperation === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.moonroofOperation === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.value === 'not-working' 
                                          ? 'bg-gradient-to-r from-red-400/10 to-orange-400/10' 
                                          : 'bg-gradient-to-r from-green-400/10 to-emerald-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div data-field="keysAvailable" className={`${missingFields.includes('keysAvailable') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('keysAvailable') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent`}>
                              KEYS AVAILABLE {missingFields.includes('keysAvailable') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: '1', label: '1 Key' },
                                { value: '2', label: '2 Keys' },
                                { value: '3', label: '3 Keys' },
                                { value: '4', label: '4+ Keys' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="keysAvailable"
                                    value={option.value}
                                    checked={conditionData.keysAvailable === option.value}
                                    onChange={(e) => handleConditionChange('keysAvailable', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                    conditionData.keysAvailable === option.value
                                      ? option.value === '1'
                                        ? 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                        : 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                      conditionData.keysAvailable === option.value
                                        ? option.value === '1'
                                          ? 'bg-red-400 text-white shadow-lg'
                                          : 'bg-green-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.keysAvailable === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.keysAvailable === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.value === '1' 
                                          ? 'bg-gradient-to-r from-red-400/10 to-orange-400/10' 
                                          : 'bg-gradient-to-r from-green-400/10 to-emerald-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div data-field="smokedInOdors" className={`${missingFields.includes('smokedInOdors') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                            <label className={`block text-lg font-bold mb-4 ${missingFields.includes('smokedInOdors') ? 'text-red-300' : 'text-white'} bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent`}>
                              SMOKED IN OR ODORS {missingFields.includes('smokedInOdors') && <span className="text-red-400">*</span>}
                            </label>
                            <div className="space-y-2">
                              {[
                                { value: 'no', label: 'No' },
                                { value: 'yes', label: 'Yes' }
                              ].map((option) => (
                                <label key={option.value} className="group cursor-pointer">
                                  <input
                                    type="radio"
                                    name="smokedInOdors"
                                    value={option.value}
                                    checked={conditionData.smokedInOdors === option.value}
                                    onChange={(e) => handleConditionChange('smokedInOdors', e.target.value)}
                                    className="sr-only"
                                  />
                                  <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                                                      conditionData.smokedInOdors === option.value
                                    ? option.value === 'no' 
                                      ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                      : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                      : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                  }`}>
                                    <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                                                            conditionData.smokedInOdors === option.value
                                        ? option.value === 'no' 
                                          ? 'bg-green-400 text-white shadow-lg'
                                          : 'bg-red-400 text-white shadow-lg'
                                        : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                    }`}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <span className={`font-medium transition-colors duration-200 ${
                                      conditionData.smokedInOdors === option.value ? 'text-white' : 'text-gray-300'
                                    }`}>
                                      {option.label}
                                    </span>
                                    {conditionData.smokedInOdors === option.value && (
                                      <div className={`absolute inset-0 rounded-lg ${
                                        option.value === 'no' 
                                          ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                          : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                      } pointer-events-none`} />
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">Vehicle Modifications</label>
                          <textarea
                            value={conditionData.vehicleModifications}
                            onChange={(e) => handleConditionChange('vehicleModifications', e.target.value)}
                            placeholder="Describe any modifications made to the vehicle..."
                            className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                          />
                        </div>
                        <div data-field="accidentHistory" className={`${missingFields.includes('accidentHistory') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                          <label className={`block text-sm font-medium mb-3 ${missingFields.includes('accidentHistory') ? 'text-red-300' : 'text-gray-300'}`}>
                            Accident History {missingFields.includes('accidentHistory') && <span className="text-red-400">*</span>}
                          </label>
                          <div className="space-y-2">
                            {[
                              { value: 'no-accidents', label: 'No accidents' },
                              { value: 'minor-accident', label: 'Minor accident' },
                              { value: 'major-accident', label: 'Major accident' },
                              { value: 'multiple-accidents', label: 'Multiple accidents' }
                            ].map((option) => (
                              <label key={option.value} className="group cursor-pointer">
                                <input
                                  type="radio"
                                  name="accidentHistory"
                                  value={option.value}
                                  checked={conditionData.accidentHistory === option.value}
                            onChange={(e) => handleConditionChange('accidentHistory', e.target.value)}
                                  className="sr-only"
                                />
                                <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                  conditionData.accidentHistory === option.value
                                    ? option.value === 'no-accidents'
                                      ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                      : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                    : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                }`}>
                                  <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                    conditionData.accidentHistory === option.value
                                      ? option.value === 'no-accidents'
                                        ? 'bg-green-400 text-white shadow-lg'
                                        : 'bg-red-400 text-white shadow-lg'
                                      : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                  }`}>
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                        </div>
                                  <span className={`font-medium transition-colors duration-200 ${
                                    conditionData.accidentHistory === option.value ? 'text-white' : 'text-gray-300'
                                  }`}>
                                    {option.label}
                                  </span>
                                  {conditionData.accidentHistory === option.value && (
                                    <div className={`absolute inset-0 rounded-lg ${
                                      option.value === 'no-accidents' 
                                        ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                        : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                    } pointer-events-none`} />
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div data-field="vehicleDrivability" className={`${missingFields.includes('vehicleDrivability') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                          <label className={`block text-sm font-medium mb-3 ${missingFields.includes('vehicleDrivability') ? 'text-red-300' : 'text-gray-300'}`}>
                            Vehicle Drivability {missingFields.includes('vehicleDrivability') && <span className="text-red-400">*</span>}
                          </label>
                          <div className="space-y-2">
                            {[
                              { value: 'drives-perfectly', label: 'Drives perfectly' },
                              { value: 'minor-issues', label: 'Minor issues' },
                              { value: 'major-issues', label: 'Major issues' },
                              { value: 'not-drivable', label: 'Not drivable' }
                            ].map((option) => (
                              <label key={option.value} className="group cursor-pointer">
                                <input
                                  type="radio"
                                  name="vehicleDrivability"
                                  value={option.value}
                                  checked={conditionData.vehicleDrivability === option.value}
                            onChange={(e) => handleConditionChange('vehicleDrivability', e.target.value)}
                                  className="sr-only"
                                />
                                <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                  conditionData.vehicleDrivability === option.value
                                    ? option.value === 'drives-perfectly'
                                      ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                      : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                    : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                }`}>
                                  <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                    conditionData.vehicleDrivability === option.value
                                      ? option.value === 'drives-perfectly'
                                        ? 'bg-green-400 text-white shadow-lg'
                                        : 'bg-red-400 text-white shadow-lg'
                                      : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                  }`}>
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                        </div>
                                  <span className={`font-medium transition-colors duration-200 ${
                                    conditionData.vehicleDrivability === option.value ? 'text-white' : 'text-gray-300'
                                  }`}>
                                    {option.label}
                                  </span>
                                  {conditionData.vehicleDrivability === option.value && (
                                    <div className={`absolute inset-0 rounded-lg ${
                                      option.value === 'drives-perfectly' 
                                        ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                        : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                    } pointer-events-none`} />
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                  {/* Mechanical & Electrical Issues */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold">Mechanical & Electrical</h3>
                    </div>
                    <div className="p-4 pt-0 space-y-4">
                        <div data-field="mechanicalElectricalStatus" className={`${missingFields.includes('mechanicalElectricalStatus') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                        <label className={`block text-sm font-medium mb-3 ${missingFields.includes('mechanicalElectricalStatus') ? 'text-red-300' : 'text-gray-300'}`}>
                          Mechanical & Electrical Issues {missingFields.includes('mechanicalElectricalStatus') && <span className="text-red-400">*</span>}
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'no-issues', label: 'No mechanical or electrical issues' },
                            { value: 'minor-issues', label: 'Minor issues (working but not perfect)' },
                            { value: 'check-engine-light', label: 'Check Engine Light Or Other Warnings' },
                            { value: 'major-issues', label: 'Major issues (needs repair)' },
                            { value: 'custom-issues', label: 'I want to describe specific issues' }
                          ].map((option) => (
                            <label key={option.value} className="group cursor-pointer">
                              <input
                                type="radio"
                                name="mechanicalElectricalStatus"
                                value={option.value}
                                checked={conditionData.mechanicalElectricalStatus === option.value}
                                onChange={(e) => handleConditionChange('mechanicalElectricalStatus', e.target.value)}
                                className="sr-only"
                              />
                              <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                conditionData.mechanicalElectricalStatus === option.value
                                  ? option.value === 'no-issues'
                                    ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                    : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                  : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                              }`}>
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                  conditionData.mechanicalElectricalStatus === option.value
                                    ? option.value === 'no-issues'
                                      ? 'bg-green-400 text-white shadow-lg'
                                      : 'bg-red-400 text-white shadow-lg'
                                    : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                }`}>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className={`font-medium transition-colors duration-200 ${
                                  conditionData.mechanicalElectricalStatus === option.value ? 'text-white' : 'text-gray-300'
                                }`}>
                                  {option.label}
                                </span>
                                {conditionData.mechanicalElectricalStatus === option.value && (
                                  <div className={`absolute inset-0 rounded-lg ${
                                    option.value === 'no-issues' 
                                      ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                      : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                  } pointer-events-none`} />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        {conditionData.mechanicalElectricalStatus === 'custom-issues' && (
                          <div className="mt-4">
                          <textarea
                            value={conditionData.mechanicalElectricalIssues}
                            onChange={(e) => handleConditionChange('mechanicalElectricalIssues', e.target.value)}
                            placeholder="Describe any mechanical or electrical issues..."
                              className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                          />
                        </div>
                        )}
                      </div>
                      
                        <div data-field="engineIssuesStatus" className={`${missingFields.includes('engineIssuesStatus') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                        <label className={`block text-sm font-medium mb-3 ${missingFields.includes('engineIssuesStatus') ? 'text-red-300' : 'text-gray-300'}`}>
                          Engine Issues {missingFields.includes('engineIssuesStatus') && <span className="text-red-400">*</span>}
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'no-engine-issues', label: 'No engine issues' },
                            { value: 'minor-engine-issues', label: 'Minor engine issues' },
                            { value: 'major-engine-issues', label: 'Major engine issues' },
                            { value: 'custom-engine-issues', label: 'I want to describe specific engine problems' }
                          ].map((option) => (
                            <label key={option.value} className="group cursor-pointer">
                              <input
                                type="radio"
                                name="engineIssuesStatus"
                                value={option.value}
                                checked={conditionData.engineIssuesStatus === option.value}
                                onChange={(e) => handleConditionChange('engineIssuesStatus', e.target.value)}
                                className="sr-only"
                              />
                              <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                conditionData.engineIssuesStatus === option.value
                                  ? option.value === 'no-engine-issues'
                                    ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                    : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                  : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                              }`}>
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                  conditionData.engineIssuesStatus === option.value
                                    ? option.value === 'no-engine-issues'
                                      ? 'bg-green-400 text-white shadow-lg'
                                      : 'bg-red-400 text-white shadow-lg'
                                    : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                }`}>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className={`font-medium transition-colors duration-200 ${
                                  conditionData.engineIssuesStatus === option.value ? 'text-white' : 'text-gray-300'
                                }`}>
                                  {option.label}
                                </span>
                                {conditionData.engineIssuesStatus === option.value && (
                                  <div className={`absolute inset-0 rounded-lg ${
                                    option.value === 'no-engine-issues' 
                                      ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                      : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                  } pointer-events-none`} />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        {conditionData.engineIssuesStatus === 'custom-engine-issues' && (
                          <div className="mt-4">
                          <textarea
                            value={conditionData.engineIssues}
                            onChange={(e) => handleConditionChange('engineIssues', e.target.value)}
                            placeholder="Describe any engine problems..."
                              className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                          />
                          </div>
                        )}
                        </div>
                      </div>
                    </div>

                  {/* Service History */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold">Service History</h3>
                    </div>
                    <div className="p-4 pt-0 space-y-4">
                        <div data-field="serviceHistoryStatus" className={`${missingFields.includes('serviceHistoryStatus') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                        <label className={`block text-sm font-medium mb-3 ${missingFields.includes('serviceHistoryStatus') ? 'text-red-300' : 'text-gray-300'}`}>
                          Service History {missingFields.includes('serviceHistoryStatus') && <span className="text-red-400">*</span>}
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'well-maintained', label: 'Well maintained with regular service' },
                            { value: 'some-maintenance', label: 'Some maintenance, but not always on schedule' },
                            { value: 'minimal-maintenance', label: 'Minimal maintenance' },
                            { value: 'custom-service', label: 'I want to describe specific service history' }
                          ].map((option) => (
                            <label key={option.value} className="group cursor-pointer">
                              <input
                                type="radio"
                                name="serviceHistoryStatus"
                                value={option.value}
                                checked={conditionData.serviceHistoryStatus === option.value}
                                onChange={(e) => handleConditionChange('serviceHistoryStatus', e.target.value)}
                                className="sr-only"
                              />
                              <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                conditionData.serviceHistoryStatus === option.value
                                  ? option.value === 'well-maintained'
                                    ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                    : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                  : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                              }`}>
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                  conditionData.serviceHistoryStatus === option.value
                                    ? option.value === 'well-maintained'
                                      ? 'bg-green-400 text-white shadow-lg'
                                      : 'bg-red-400 text-white shadow-lg'
                                    : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                }`}>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className={`font-medium transition-colors duration-200 ${
                                  conditionData.serviceHistoryStatus === option.value ? 'text-white' : 'text-gray-300'
                                }`}>
                                  {option.label}
                                </span>
                                {conditionData.serviceHistoryStatus === option.value && (
                                  <div className={`absolute inset-0 rounded-lg ${
                                    option.value === 'well-maintained' 
                                      ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                      : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                  } pointer-events-none`} />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        {conditionData.serviceHistoryStatus === 'custom-service' && (
                          <div className="mt-4">
                          <textarea
                            value={conditionData.serviceHistory}
                            onChange={(e) => handleConditionChange('serviceHistory', e.target.value)}
                            placeholder="Describe maintenance history, oil changes, major services..."
                              className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                          />
                        </div>
                        )}
                      </div>
                      
                        <div data-field="otherServiceIssuesStatus" className={`${missingFields.includes('otherServiceIssuesStatus') ? 'ring-2 ring-red-500 bg-red-500/10 rounded-lg p-3' : ''}`}>
                        <label className={`block text-sm font-medium mb-3 ${missingFields.includes('otherServiceIssuesStatus') ? 'text-red-300' : 'text-gray-300'}`}>
                          Other Service Issues {missingFields.includes('otherServiceIssuesStatus') && <span className="text-red-400">*</span>}
                        </label>
                        <div className="space-y-2">
                          {[
                            { value: 'no-other-issues', label: 'No other service issues' },
                            { value: 'some-service-issues', label: 'Some service issues' },
                            { value: 'custom-other-issues', label: 'I want to describe specific concerns' }
                          ].map((option) => (
                            <label key={option.value} className="group cursor-pointer">
                              <input
                                type="radio"
                                name="otherServiceIssuesStatus"
                                value={option.value}
                                checked={conditionData.otherServiceIssuesStatus === option.value}
                                onChange={(e) => handleConditionChange('otherServiceIssuesStatus', e.target.value)}
                                className="sr-only"
                              />
                              <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                conditionData.otherServiceIssuesStatus === option.value
                                  ? option.value === 'no-other-issues'
                                    ? 'border-green-300 bg-green-400/20 shadow-lg shadow-green-400/25'
                                    : 'border-red-300 bg-red-400/20 shadow-lg shadow-red-400/25'
                                  : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                              }`}>
                                <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                  conditionData.otherServiceIssuesStatus === option.value
                                    ? option.value === 'no-other-issues'
                                      ? 'bg-green-400 text-white shadow-lg'
                                      : 'bg-red-400 text-white shadow-lg'
                                    : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                }`}>
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <span className={`font-medium transition-colors duration-200 ${
                                  conditionData.otherServiceIssuesStatus === option.value ? 'text-white' : 'text-gray-300'
                                }`}>
                                  {option.label}
                                </span>
                                {conditionData.otherServiceIssuesStatus === option.value && (
                                  <div className={`absolute inset-0 rounded-lg ${
                                    option.value === 'no-other-issues' 
                                      ? 'bg-gradient-to-r from-green-400/10 to-emerald-400/10' 
                                      : 'bg-gradient-to-r from-red-400/10 to-orange-400/10'
                                  } pointer-events-none`} />
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        {conditionData.otherServiceIssuesStatus === 'custom-other-issues' && (
                          <div className="mt-4">
                          <textarea
                            value={conditionData.otherServiceIssues}
                            onChange={(e) => handleConditionChange('otherServiceIssues', e.target.value)}
                            placeholder="Any other service-related concerns..."
                              className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                          />
                          </div>
                        )}
                        </div>
                      </div>
                    </div>

                  {/* Tire Replacement History */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold">Tire Replacement History</h3>
                    </div>
                    <div className="p-4 pt-0 space-y-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <button
                            type="button"
                            onClick={() => {
                              handleConditionChange('originalTires', true);
                              handleConditionChange('tiresReplaced', false);
                            }}
                            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                              conditionData.originalTires 
                                ? 'bg-green-600 text-white' 
                                : 'bg-white/15 border border-white/40 text-gray-300 hover:bg-white/25'
                            }`}
                          >
                            Still Original Tires
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleConditionChange('originalTires', false);
                              handleConditionChange('tiresReplaced', true);
                            }}
                            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                              !conditionData.originalTires 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white/15 border border-white/40 text-gray-300 hover:bg-white/25'
                            }`}
                          >
                            Tires Replaced
                          </button>
                        </div>
                        
                        {!conditionData.originalTires && conditionData.tiresReplaced && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">How many miles ago?</label>
                              <input
                                type="number"
                                value={conditionData.replacementMiles}
                                onChange={(e) => handleConditionChange('replacementMiles', e.target.value)}
                                placeholder="Miles since replacement"
                                className="w-full px-3 py-2 bg-white/15 border border-white/40 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-3">How many tires replaced?</label>
                              <div className="space-y-2">
                                {[
                                  { value: '1', label: '1 tire' },
                                  { value: '2', label: '2 tires' },
                                  { value: '3', label: '3 tires' },
                                  { value: '4', label: '4 tires' }
                                ].map((option) => (
                                  <label key={option.value} className="group cursor-pointer">
                                    <input
                                      type="radio"
                                      name="replacementCount"
                                      value={option.value}
                                      checked={conditionData.replacementCount === option.value}
                                onChange={(e) => handleConditionChange('replacementCount', e.target.value)}
                                      className="sr-only"
                                    />
                                    <div className={`relative flex items-center p-3 rounded-lg border-2 transition-all duration-200 ${
                                      conditionData.replacementCount === option.value
                                        ? 'border-gray-400 bg-gray-500/20 shadow-lg shadow-gray-500/25'
                                        : 'border-white/20 bg-white/10 hover:border-white/40 hover:bg-white/15'
                                    }`}>
                                      <div className={`flex items-center justify-center w-6 h-6 rounded-full mr-3 transition-all duration-200 ${
                                        conditionData.replacementCount === option.value
                                          ? 'bg-gray-500 text-white shadow-lg'
                                          : 'bg-white/20 text-gray-400 group-hover:bg-white/30'
                                      }`}>
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                      <span className={`font-medium transition-colors duration-200 ${
                                        conditionData.replacementCount === option.value ? 'text-white' : 'text-gray-300'
                                      }`}>
                                        {option.label}
                                      </span>
                                      {conditionData.replacementCount === option.value && (
                                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-gray-500/10 to-slate-500/10 pointer-events-none" />
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  {/* Photo Upload Section */}
                  <div className="bg-white/10 rounded-lg border border-white/20">
                    <div className="p-4 flex items-center text-white">
                      <h3 className="text-lg font-semibold">Vehicle Photos</h3>
                      {uploadedPhotos.length > 0 && (
                        <span className="ml-2 bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                          {uploadedPhotos.length}
                        </span>
                      )}
                    </div>
                    <div className="p-4 pt-0">
                        <div className="mb-4 p-3 bg-blue-500/20 border border-blue-400/30 rounded-lg">
                          <p className="text-sm text-blue-200">
                            <strong>Photo Tips:</strong> Upload clear photos from multiple angles: front, back, both sides, interior dashboard, tires, and any damage. Take photos in good lighting if possible.
                          </p>
                        </div>
                        
                        <div
                          className="border-2 border-dashed border-white/30 rounded-lg p-6 text-center hover:border-white/50 transition-colors cursor-pointer"
                          onDrop={(e) => {
                            e.preventDefault();
                            handlePhotoUpload(e.dataTransfer.files);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onClick={() => document.getElementById('photo-upload').click()}
                        >
                          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-white mb-2">Drag and drop photos here, or click to select</p>
                          <p className="text-gray-400 text-sm">JPG, PNG up to 10MB each</p>
                        </div>
                        
                        <input
                          id="photo-upload"
                          type="file"
                          multiple
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(e.target.files)}
                        />

                        {uploadedPhotos.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-white font-medium mb-3">Uploaded Photos ({uploadedPhotos.length})</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {uploadedPhotos.map((photo) => (
                                <div key={photo.id} className="relative group">
                                  <img
                                    src={photo.url}
                                    alt="Vehicle"
                                    className="w-full h-24 object-cover rounded-lg border border-white/20"
                                    style={{
                                      imageOrientation: 'from-image',
                                      objectFit: 'cover'
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(photo.id)}
                                    className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  {/* Form Actions */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      disabled={submitting}
                      className="flex-1 px-6 py-3 border border-white/40 text-white rounded-lg hover:bg-white/15 transition-colors backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Back to Features & Options
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Submitting...' : 'Submit Trade-In Evaluation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Submission Overlay */}
      {(submitting || submitted) && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                        <div className="bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
            {submitting && (
              <div className="space-y-6">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-white">Submitting Your Trade-In Evaluation</h3>
                  <p className="text-gray-300">Please wait while we process your information...</p>
                </div>
              </div>
            )}
            
            {submitted && (
              <div className="space-y-6">
                <div className="w-16 h-16 mx-auto">
                  <svg className="w-16 h-16 text-green-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-white">Trade-In Evaluation Submitted Successfully!</h3>
                  <p className="text-gray-300">A dealership representative will contact you within 24 hours.</p>
                  <p className="text-sm text-gray-400">Redirecting you to the homepage...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile VIN Scanner Modal - Hidden on Windows */}
      {showVinScanner && !isWindows && (
        <MobileVinScanner 
          onVinScanned={async (scannedVin) => {
            const value = scannedVin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
            if (value.length === 17) {
              setVin(value);
              setError(null);
              setShowVinScanner(false);
              
              const validation = validateVIN(value);
              setVinValidation(validation);
              
              // Auto-submit if VIN is valid - use the scanned value directly
              if (validation.valid) {
                setDecodingVin(true);
                
                const result = await decodeVINWithNHTSA(value);
                setVinResult(result);
                
                if (result.valid) {
                  // Check if VIN already exists for this dealership
                  const path = window.location.pathname;
                  const dealershipIdentifier = path.split('/')[2];
                  const dealershipId = dealership?._id || dealershipIdentifier;
                  const vinExists = await checkVinExists(value, dealershipId);
                  
                  if (vinExists) {
                    setError('This VIN has already been submitted for evaluation. Please contact the dealership if you need to update your submission.');
                    setDecodingVin(false);
                    return;
                  }
                  
                  setDecodingVin(false);
                  setCurrentStep(2);
                } else {
                  setDecodingVin(false);
                  setError(result.error);
                }
              }
            }
          }}
          onError={(error) => {
            setError(error);
            setShowVinScanner(false);
          }}
          onClose={() => setShowVinScanner(false)}
        />
      )}
    </div>
  );
} 